// Move the existing vue-multiselect menu instead of duplicating its options or
// events, preserving selection, search, keyboard navigation and ARIA IDs.
const instances = new WeakMap();

export function mountFloatingSelect(element, select) {
    const list = select?.$refs.list;
    if (!list) return () => {};
    const marker = document.createComment('floating-select');
    list.parentNode.insertBefore(marker, list);
    const originalStyle = list.getAttribute('style');
    let host = null;
    let frame = null;
    let destroyed = false;
    let observer = null;

    function position() {
        if (!host || destroyed) return;
        const rect = element.getBoundingClientRect();
        const width = document.documentElement.clientWidth;
        const height = window.innerHeight;
        if (!rect.width || rect.bottom <= 0 || rect.top >= height || rect.right <= 0 || rect.left >= width) {
            select.deactivate(); return;
        }
        // Close if scrolling has hidden the input inside its table or modal.
        for (let parent = element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
            const style = window.getComputedStyle(parent);
            if (/(auto|scroll|hidden)/.test(`${style.overflowX} ${style.overflowY}`)) {
                const bounds = parent.getBoundingClientRect();
                if (rect.bottom <= bounds.top || rect.top >= bounds.bottom || rect.right <= bounds.left || rect.left >= bounds.right) {
                    select.deactivate(); return;
                }
            }
        }
        const below = height - rect.bottom - 8;
        const above = rect.top - 8;
        const opensAbove = below < 240 && above > below;
        const menuHeight = Math.max(0, Math.min(select.maxHeight || 300, opensAbove ? above : below));
        select.optimizedHeight = menuHeight;
        const menuWidth = Math.min(rect.width, Math.max(0, width - 16));
        const left = Math.max(8, Math.min(rect.left, width - menuWidth - 8));
        Object.assign(list.style, {
            position: 'fixed', left: `${left}px`, width: `${menuWidth}px`,
            maxHeight: `${menuHeight}px`, margin: '0', pointerEvents: 'auto', zIndex: '1060',
            top: opensAbove ? 'auto' : `${rect.bottom}px`,
            bottom: opensAbove ? `${height - rect.top}px` : 'auto',
            borderTop: '1px solid #e8e8e8',
        });
    }
    function schedule(event) {
        if (!host || (event && list.contains(event.target))) return;
        if (frame !== null) return;
        frame = window.requestAnimationFrame(() => { frame = null; position(); });
    }
    function outside(event) {
        if (host && !element.contains(event.target) && !list.contains(event.target)) select.deactivate();
    }
    function close() {
        window.removeEventListener('scroll', schedule, true);
        window.removeEventListener('resize', schedule);
        document.removeEventListener('mousedown', outside, true);
        if (observer) { observer.disconnect(); observer = null; }
        if (frame !== null) { window.cancelAnimationFrame(frame); frame = null; }
        if (marker.parentNode) marker.parentNode.insertBefore(list, marker.nextSibling);
        if (originalStyle === null) list.removeAttribute('style');
        else list.setAttribute('style', originalStyle);
        if (host?.parentNode) host.parentNode.removeChild(host);
        host = null;
    }
    async function open() {
        await select.$nextTick();
        if (destroyed || !select.isOpen || host) return;
        host = document.createElement('div');
        host.className = 'multiselect order-floating-menu';
        Object.assign(host.style, { position: 'fixed', width: '0', height: '0', minHeight: '0', zIndex: '1060', pointerEvents: 'none' });
        host.style.font = window.getComputedStyle(element).font;
        document.body.appendChild(host);
        host.appendChild(list);
        window.addEventListener('scroll', schedule, true);
        window.addEventListener('resize', schedule);
        document.addEventListener('mousedown', outside, true);
        if (typeof ResizeObserver !== 'undefined') { observer = new ResizeObserver(schedule); observer.observe(element); }
        position();
    }
    const unwatch = select.$watch('isOpen', value => { if (value) open(); else close(); });
    if (select.isOpen) open();
    return () => { destroyed = true; unwatch(); close(); if (marker.parentNode) marker.parentNode.removeChild(marker); };
}

export default {
    inserted(element, binding, vnode) {
        instances.set(element, mountFloatingSelect(element, vnode.componentInstance));
    },
    unbind(element) { instances.get(element)?.(); instances.delete(element); },
};
